import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import { Modal } from "../components/ui";
import BillingAction from "../actions/BillingAction";
import SelectPlanModal from "../team/component/SelectPlanModal";

const UpgradePlanModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const subscriptionStatus =
    useSelector((state: any) => state.billingReducer.get("subscriptionStatus")) || fromJS({});
  const plans = useSelector((state: any) => state.billingReducer.get("plans")) || fromJS([]);
  const showUpgradePlan = useSelector(
    (state: any) => state.billingReducer.get("showUpgradePlan") || false
  );
  const requiredEntitlement =
    useSelector((state: any) => state.billingReducer.get("requiredEntitlement")) || fromJS({});
  const isAiTokenQuotaUpgrade = Boolean(requiredEntitlement?.get?.("aiContentTokensPerMonth"));

  const currentPlanKey = subscriptionStatus.get("planKey");
  const currentPlan = plans.find((item) => item.get("key") === currentPlanKey) || fromJS({});

  const handleClickUpgrade = () => {
    dispatch(BillingAction.showSelectPlanDialog());
    dispatch(BillingAction.hideUpgradePlanDialog());
  };

  const handleClose = () => {
    dispatch(BillingAction.hideUpgradePlanDialog());
  };

  return (
    <>
      <Modal
        isOpen={showUpgradePlan}
        onClose={handleClose}
        title={t("Upgrade Plan")}
        position="center"
        className="w-auto max-w-[1200px] max-h-[98vh]"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 rounded-full bg-gray-100 dark:bg-gray-900 p-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-blue-600 dark:text-blue-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h4l3 8 4-16 3 8h4"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2
                id="upgrade-modal-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                {t("Upgrade your plan")}
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {isAiTokenQuotaUpgrade
                  ? t(
                      "You have reached your monthly AI token limit for Content Studio. Upgrade your plan for a higher allowance."
                    )
                  : t("This feature requires an upgrade plan.")}
                {!isAiTokenQuotaUpgrade ? (
                  <>
                    <br />
                    {t("Upgrade to unlock unlimited access, priority support, and team seats.")}
                  </>
                ) : null}
              </p>
              <ul className="mt-4 grid grid-cols-1 gap-2 text-sm text-gray-700 dark:text-gray-200">
                <li className="flex items-center gap-2">
                  <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                    ✓
                  </span>
                  {t("Unlimited uses of this feature")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                    ✓
                  </span>
                  {t("Priority email & chat support")}
                </li>
                <li className="flex items-center gap-2">
                  <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                    ✓
                  </span>
                  {t("Team seats & billing")}
                </li>
              </ul>
            </div>
          </div>
          {currentPlan.get("name") ? (
            <div className="mt-4 p-2 text-gray-500 dark:text-gray-400">
              {t("Your current plan")}:{" "}
              <strong className="text-gray-700 dark:text-gray-200">
                {currentPlan.get("name")}
              </strong>
            </div>
          ) : null}
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              onClick={handleClickUpgrade}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {t("Upgrade now")}
            </button>
            <button
              onClick={handleClose}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium bg-transparent border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40 focus:outline-none"
            >
              {t("Maybe later")}
            </button>
          </div>
        </div>
      </Modal>
      <SelectPlanModal />
    </>
  );
};

export default UpgradePlanModal;
